import React, { useEffect } from 'react'
import { Box } from '@node-space/ui-components'
import { useDynamicQuestionsMutation } from 'hooks/mutations/useDynamicQuestionsMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { FormSubmitError } from 'pages/Verification/_components/FormSubmitError'
import { DynamicQuestionCode } from 'types/dynamicFields'
import { BaseErrorResponse } from 'types/errors'
import { DynamicQuestionFieldTypes } from '../helpers/constants'
import { DynamicQuestionsStep, StepUnsavedChanges } from '../helpers/types'
import { useDynamicQuestionsForm } from '../helpers/useDynamicQuestionsForm'
import {
  getAdditionalFieldKey,
  getContactEmailFieldKey,
  getContactLastNameFieldKey,
} from '../helpers/utils'
import { DynamicContactQuestion } from './DynamicContactQuestions'
import { DynamicQuestion } from './DynamicQuestion'

export interface BusinessComplianceStepProps {
  questions: DynamicQuestionsStep
  unsavedChanges: StepUnsavedChanges
  saveUnsavedChanges: (formValues: StepUnsavedChanges) => void
  onContinue: (updatedQuestions?: DynamicQuestionsStep) => void
}

export const BusinessComplianceStep = ({
  questions,
  unsavedChanges,
  saveUnsavedChanges,
  onContinue,
}: BusinessComplianceStepProps) => {
  const { requestError, handleRequestError, resetRequestError } = useMappedErrorMessage()
  const { formFields, form, defaultValues, formValues, getUpdatedQuestions } =
    useDynamicQuestionsForm(questions, unsavedChanges)
  const { mutate: submitDynamicQuestions, isPending: isSubmittingQuestions } =
    useDynamicQuestionsMutation()

  const {
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = form

  const onSubmitStep = () => {
    resetRequestError()
    if (!unsavedChanges && !isDirty) {
      onContinue()
      return
    }
    const updatedQuestions = getUpdatedQuestions()
    submitDynamicQuestions(updatedQuestions?.payload, {
      onSuccess: () => {
        onContinue(updatedQuestions?.mapping)
      },
      onError: (error: BaseErrorResponse) => handleRequestError(error, 'submitDynamicQuestions'),
    })
  }

  useEffect(() => {
    resetRequestError()
    reset(defaultValues)
  }, [questions])

  useEffect(() => {
    isDirty && saveUnsavedChanges(formValues)
  }, [isDirty, formValues])

  return (
    <form onSubmit={handleSubmit(onSubmitStep)}>
      {requestError?.show && (
        <Box paddingT={24}>
          <FormSubmitError
            errorCode={requestError?.errorCode}
            sentryTraceId={requestError?.sentryTraceId}
          />
        </Box>
      )}
      <Box divideChildren>
        {Object.keys(questions)?.map((questionCode: DynamicQuestionCode) => {
          const question = questions?.[questionCode]
          return (
            <Box key={questionCode} paddingY={24}>
              {question?.type === DynamicQuestionFieldTypes.CONTACT ? (
                <DynamicContactQuestion
                  code={questionCode}
                  firstNameField={formFields?.[questionCode]}
                  lastNameField={formFields?.[getContactLastNameFieldKey(questionCode)]}
                  emailField={formFields?.[getContactEmailFieldKey(questionCode)]}
                />
              ) : (
                <DynamicQuestion
                  code={questionCode}
                  question={question}
                  formField={formFields?.[questionCode]}
                  {...(question?.additionalQuestion && {
                    additionalField: formFields?.[getAdditionalFieldKey(questionCode)],
                  })}
                />
              )}
            </Box>
          )
        })}
      </Box>
      <FormSubmitButton
        loading={isSubmittingQuestions}
        disabled={!!Object.keys(errors)?.length}
        testId="continue-button"
      />
    </form>
  )
}
